'use client';

import { useQuery } from '@tanstack/react-query';
import flagsmith from 'flagsmith';
import { FlagsmithProvider as Provider } from 'flagsmith/react';
import { IFlagsmith } from 'flagsmith/types';
import { useEffect, useState } from 'react';

import { getUserTraitsQueryOptions } from '@/_api/account/getTraits';
import { getFlagsmithTraits } from '@/_lib/featureflags/utils';
import { getRuntimeEnv } from '@/_utils/environment';

const { flagsmithId } = getRuntimeEnv();

interface FlagsmithProviderProps {
  children: React.ReactElement;
}

export const FlagsmithProvider = ({ children }: FlagsmithProviderProps) => {
  const { data: userTraits, isLoading: isUserTraitsLoading } = useQuery(getUserTraitsQueryOptions());

  const [flagsmithState, setFlagsmithState] = useState<IFlagsmith | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const initFlagsmith = async () => {
      if (isUserTraitsLoading || typeof window === 'undefined') return;

      const { traits, identity } = await getFlagsmithTraits({ userTraits });

      flagsmith
        .init({
          environmentID: flagsmithId!,
          traits,
          identity
        })
        .then(() => {
          setFlagsmithState(flagsmith);
          setIsLoading(false);
        })
        .catch(() => {
          setIsLoading(false);
        });
    };

    initFlagsmith();
  }, [userTraits, isUserTraitsLoading]);

  if (isLoading || !flagsmithState) {
    return null;
  }

  return <Provider flagsmith={flagsmithState}>{children}</Provider>;
};
